import { createRole, getRoleListWithPagination } from '@app/api/role.api';
import { AddRoleModal } from '@app/components/common/Modal/AddRole/AddRoleModal';
import { PermissionTypes } from '@app/constants/enums/permission';
import { notificationController } from '@app/controllers/notificationController';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { useDialog } from '@app/hooks/useDialog';
import { useLoader } from '@app/hooks/useLoader';
import { ANY_OBJECT } from '@app/interfaces/interfaces';
import { IRole } from '@app/interfaces/role';
import { getPermissionType } from '@app/utils/utils';
import { Form, TablePaginationConfig } from 'antd';
import { QueryTable } from 'api/table.api';
import { Button } from 'components/common/buttons/Button/Button';
import { FilterIcon } from 'components/common/icons/FilterIcon';
import { Table } from 'components/common/Table/Table';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { roleColumns } from './Column';
import ExpandedRowRender from './ExpandedRow/ExpandedRow';
import * as S from './RoleTable.styles';

export const RoleTable: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { dialogOpen, handleDialogOpen, handleDialogClose } = useDialog();
  const { loader, handleLoaderOpen, handleLoaderClose } = useLoader();
  const [initialPagination, setInitialPagination] = useState<QueryTable>({
    page: 1,
    limit: 10,
  });
  const [tableData, setTableData] = useState<{ data: IRole[]; pagination: QueryTable; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const [filteredTableData, setFilteredTableData] = useState<{
    data: IRole[];
    pagination: QueryTable;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const [query, setQuery] = useState('');
  const location = useLocation();
  const params = useParams();
  const [permission, setPermission] = useState(0);
  const userPermission = useAppSelector((state) => state.user.user?.role?.permissions);

  const fetch = useCallback((pagination: QueryTable) => {
    setTableData((tableData) => ({ ...tableData, loading: true }));
    const payload = {
      page: pagination.page,
      limit: pagination.limit,
    };
    getRoleListWithPagination(payload)
      .then((res) => {
        setTableData((tableData) => ({
          ...tableData,
          data: res.results,
          pagination: {
            page: res.page,
            limit: res.limit,
            total: res.totalResults,
          },
          loading: false,
        }));
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
      });
  }, []);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const newPagination = {
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
    };
    setInitialPagination(newPagination);
    fetch(newPagination);
  };

  // const handleDeleteRow = (rowId: string) => {
  //   handleLoaderOpen();
  //   deleteRole(rowId)
  //     .then(() => {
  //       handleLoaderClose();
  //       handleDialogClose();
  //       notificationController.success({ message: t('roles.table.deleteSuccess') });
  //       fetch(initialPagination);
  //     })
  //     .catch((err) => {
  //       notificationController.error({ message: err.message });
  //       handleLoaderClose();
  //     });
  // };

  const columns = [
    ...roleColumns?.map((item) => {
      item.title = t(item.title);
      return item;
    }),
    // {
    //   title: t('tables.actions'),
    //   dataIndex: 'actions',
    //   width: 'auto',
    //   render: (text: string, record: IRole) => {
    //     return (
    //       <Space>
    //         <>
    //           {!record.isAdministrator && (
    //             <Popconfirm title={t('roles.table.deleteInfo')} onConfirm={() => handleDeleteRow(record.id)}>
    //               <Button type="default" danger>
    //                 {t('common.delete')}
    //               </Button>
    //             </Popconfirm>
    //           )}
    //         </>
    //       </Space>
    //     );
    //   },
    // },
  ];

  // const tableColumns = () => {
  //   if (permission === PermissionTypes.READ) {
  //     const tableActionToRemove = columns.findIndex((column) => column.dataIndex === 'actions');
  //     if (tableActionToRemove >= 0) {
  //       columns.splice(tableActionToRemove, 1);
  //     }
  //   }

  //   return columns;
  // };

  const onFinish = (values: ANY_OBJECT) => {
    handleLoaderOpen();
    const data = {
      role: values.role,
    };
    createRole(data)
      .then(() => {
        handleLoaderClose();
        handleDialogClose();
        notificationController.success({ message: t('roles.table.createSuccess') });
        fetch(initialPagination);
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
        handleLoaderClose();
      });
  };

  useEffect(() => {
    if (query) {
      const data = [...tableData.data];
      const filtered = data.filter((item) => {
        return item && item.role.toString().toLowerCase().indexOf(query.toLowerCase()) >= 0;
      });
      setFilteredTableData((tableData) => ({
        ...tableData,
        data: filtered,
        pagination: { ...tableData.pagination, total: filtered.length },
      }));
    }
  }, [query, tableData.data]);

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch, initialPagination]);

  useEffect(() => {
    if (userPermission) {
      const permission = getPermissionType(location, params, userPermission);
      setPermission(permission);
    }
  }, [location, params, userPermission]);

  return (
    <Form form={form} component={false}>
      <S.TableActionWrapper>
        {permission === PermissionTypes.READWRITE && (
          <>
            <Button block type="primary" className="addRole" onClick={handleDialogOpen}>
              {t('roles.table.addRole')}
            </Button>
            <AddRoleModal loading={loader} isOpen={dialogOpen} onOpenChange={handleDialogClose} onFinish={onFinish} />
          </>
        )}
        <S.InputSearch
          placeholder={t('header.search')}
          onChange={(event) => setQuery(event.target.value)}
          filter={!query ? <S.Btn size="small" type={'text'} aria-label="Filter" icon={<FilterIcon />} /> : null}
          allowClear
          enterButton={null}
          addonAfter={null}
        />
      </S.TableActionWrapper>
      <Table
        rowKey="id"
        bordered
        // columns={tableColumns()}
        columns={columns}
        dataSource={query ? filteredTableData.data : tableData.data}
        rowClassName="editable-row"
        pagination={{
          ...(query
            ? (filteredTableData.pagination as TablePaginationConfig)
            : (tableData.pagination as TablePaginationConfig)),
          locale: { items_per_page: `/ ${t('common.page')}` },
        }}
        onChange={handleTableChange}
        loading={query ? filteredTableData.loading : tableData.loading}
        scroll={{ x: 800 }}
        locale={{ emptyText: t('common.noData') }}
        expandable={{
          expandedRowRender: (record) => (
            <ExpandedRowRender
              record={record}
              tableData={tableData}
              initialPagination={initialPagination}
              setInitialPagination={setInitialPagination}
              fetch={fetch}
              permission={permission}
            />
          ),
        }}
      />
    </Form>
  );
};
